import useStores from 'shared/hooks/use-stores';
import { useGetUserIncentiveEligibilityQuery } from 'types/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ValidIncentiveValue, isValidIncentiveValue } from '../shared/incentive-types';

export type CashBackIncentiveData = {
  eligible: boolean;
  incentive?: {
    id: string;
    name: string;
    description?: string | null;
    amountCents: number;
    cohortName: string;
    active: boolean;
  } | null;
};

export function useCashBackIncentives(): {
  incentiveData: CashBackIncentiveData | null;
  loading: boolean;
  error: any;
  shouldShowBanner: boolean;
  cashBackIncentiveValue: ValidIncentiveValue | undefined;
} {
  const { Cart, User } = useStores();
  const flags = useFlags();

  const cashBackRedemptionEnabled = flags['fintech.incentives.redemption.fe.rollout'] ?? false;

  const shouldSkip = !cashBackRedemptionEnabled || !User.exists || !User.id || !Cart.order.dispensary?.id;

  const { data, loading, error } = useGetUserIncentiveEligibilityQuery({
    skip: shouldSkip,
    variables: {
      externalId: User.id,
      dispensaryId: Cart.order.dispensary?.id,
    },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const incentiveData = cashBackRedemptionEnabled ? data?.getUserIncentiveEligibility ?? null : null;
  const amountCents = incentiveData?.incentive?.amountCents;

  // Convert amountCents to ValidIncentiveValue (dollars) - only if valid
  const calculatedValue = amountCents !== undefined ? Math.round(amountCents / 100) : undefined;
  const cashBackIncentiveValue = isValidIncentiveValue(calculatedValue) ? calculatedValue : undefined;

  // Only show banner if the converted value is valid to prevent showing banners with undefined incentive values
  const shouldShowBanner = Boolean(
    cashBackRedemptionEnabled && incentiveData?.eligible && cashBackIncentiveValue !== undefined
  );

  return {
    incentiveData,
    loading,
    error,
    shouldShowBanner,
    cashBackIncentiveValue,
  };
}
